import { CreateProfileDto, UpdateUserDto } from '@sr-sdks/api-sdk-axios';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  sonner,
} from '@sr/ui-library';
import { useEffect, useMemo } from 'react';
import { Path, useFormContext } from 'react-hook-form';
import {
  UpdateUserProfileInput,
  useUpdateUserProfile,
} from '../../../api/permissions/userApi';
import UnsavedChangesPrompt from '../../../components/UnsavedChangesPrompt';
import ViewModeField from '../../../components/ViewModeField';
import { useAuth } from '../../../hooks/useAuth';
import { userService } from '../../../reducers/permissions/userSlice';
import { useTypedSelector } from '../../../stateStore';
import { AuthHelper } from '../../../utils/auth/authHelper';
import { ApiError } from '../../../utils/httpError';
import { MSR3_PROFILE_FORM_ID } from './formIds';

const { toast } = sonner;

export type ProfileFields = {
  emailAddress: string;
  firstName: string;
  lastName: string;
  middleName: string;
};

export const EMPTY_PROFILE_FIELDS: ProfileFields = {
  emailAddress: '',
  firstName: '',
  lastName: '',
  middleName: '',
};

const PROFILE_FIELD_ORDER: Path<ProfileFields>[] = [
  'firstName',
  'middleName',
  'lastName',
  'emailAddress',
];

const profileFieldsFromUser = (
  userMe: ReturnType<typeof userService.selectors.userMe>,
  fallbackEmail?: string,
): ProfileFields => ({
  emailAddress: userMe?.emailAddress ?? fallbackEmail ?? '',
  firstName: userMe?.firstName ?? '',
  lastName: userMe?.lastName ?? '',
  middleName: userMe?.middleName ?? '',
});

type MSR3ProfileSettingsFormProps = {
  onSaveSuccess?: () => void;
};

/**
 * Profile settings form body. The `useForm` instance and `<FormProvider>`
 * live in `MyProfile` so that the lifted Update/Revert buttons and the form
 * inputs share a single source of truth — no observer-callback round-trip
 * per keystroke, no `DEFAULT_PROFILE_FORM_STATE` constant.
 */
export const MSR3ProfileSettingsForm = ({
  onSaveSuccess,
}: MSR3ProfileSettingsFormProps): JSX.Element => {
  const { user } = useAuth();
  const userMe = useTypedSelector(userService.selectors.userMe);
  const updateProfileMutation = useUpdateUserProfile();
  const isUpdating = updateProfileMutation.isPending;

  // PRECONDITION: must be rendered inside `<FormProvider>` (see `MyProfile.tsx`).
  // We don't runtime-assert this because the assertion would itself become a
  // conditional hook boundary; rely on the static call-site contract instead.
  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
    setError,
    setFocus,
  } = useFormContext<ProfileFields>();

  const userMeId = userMe?._id;
  const isUserInternal = useMemo(() => {
    return userMe ? new AuthHelper(userMe).currentUserIsInternal : false;
  }, [userMe]);

  const currentProfileFields = useMemo(
    () => profileFieldsFromUser(userMe, user?.email),
    [user?.email, userMe],
  );

  // Reset on stable user id so a background refetch of the same user doesn't
  // clobber an in-flight edit. The first `userMe` arrival also seeds the form.
  useEffect(() => {
    if (isDirty) return;
    reset(currentProfileFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMeId, reset]);

  const focusFirstInvalidField = (
    invalidFields: Path<ProfileFields>[],
  ): void => {
    const first = PROFILE_FIELD_ORDER.find((field) =>
      invalidFields.includes(field),
    );

    if (first) setFocus(first);
  };

  const applyServerError = (error: ApiError) => {
    const message = error.message || 'Unable to update your profile.';

    // Only attach to the email field when the backend has unambiguously
    // identified it as the offending input. A loose `/email/i` regex on the
    // human-readable message would mis-classify generic errors that happen
    // to mention "email" (e.g. "email service unavailable").
    const isEmailConflict =
      error.status === 409 || error.code === 'EMAIL_IN_USE';

    if (isEmailConflict) {
      setError('emailAddress', { message, type: 'server' });
      focusFirstInvalidField(['emailAddress']);

      return;
    }

    toast.error(message);
  };

  const submitForm = async (formData: ProfileFields) => {
    if (isUpdating) return;

    const variantInput: UpdateUserProfileInput = userMeId
      ? {
          id: userMeId,
          updateUserDto: {
            emailAddress: formData.emailAddress,
            firstName: formData.firstName,
            imageUri: user?.picture,
            lastName: formData.lastName,
            middleName: formData.middleName,
          } satisfies UpdateUserDto,
        }
      : {
          createProfileDto: {
            firstName: formData.firstName,
            imageUri: user?.picture,
            lastName: formData.lastName,
            middleName: formData.middleName,
          } satisfies CreateProfileDto,
        };

    try {
      await updateProfileMutation.mutateAsync(variantInput);

      reset(formData);
      onSaveSuccess?.();
    } catch (error) {
      if (error instanceof ApiError) {
        applyServerError(error);

        return;
      }

      toast.error('Unable to update your profile. Please try again.');
    }
  };

  const handleReset = () => {
    reset(currentProfileFields);
  };

  const handleInvalid = () => {
    focusFirstInvalidField(Object.keys(errors) as Path<ProfileFields>[]);
  };

  const hasUnsavedChanges = isDirty && !isUpdating;

  const emailField = isUserInternal ? (
    <ViewModeField title="Email Address" value={userMe?.emailAddress ?? ''} />
  ) : (
    <FormField
      name="emailAddress"
      control={control}
      render={({ field }) => (
        <FormItem>
          <FormLabel asterisk className="text-inherit">
            Email Address
          </FormLabel>
          <FormControl>
            <Input
              {...field}
              aria-required
              autoComplete="email"
              inputMode="email"
              spellCheck={false}
              type="email"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <form
      id={MSR3_PROFILE_FORM_ID}
      className="flex w-full flex-col items-stretch gap-5 self-stretch"
      onReset={handleReset}
      onSubmit={handleSubmit(submitForm, handleInvalid)}
    >
      {hasUnsavedChanges && (
        <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />
      )}
      <div className="grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
        <FormField
          name="firstName"
          control={control}
          render={({ field }) => (
            <FormItem>
              <FormLabel asterisk className="text-inherit">
                First Name
              </FormLabel>
              <FormControl>
                <Input {...field} aria-required autoComplete="given-name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="middleName"
          control={control}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-inherit">Middle Name</FormLabel>
              <FormControl>
                <Input {...field} autoComplete="additional-name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="lastName"
          control={control}
          render={({ field }) => (
            <FormItem>
              <FormLabel asterisk className="text-inherit">
                Last Name
              </FormLabel>
              <FormControl>
                <Input {...field} aria-required autoComplete="family-name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {emailField}
      </div>
    </form>
  );
};
